import { useMemo, useState } from "react";
import { ExportModal } from "@/components";
import {
  ApiResponse,
  axiosInstance,
  createCsvContent,
  downloadCsvFile,
  handleAxiosError,
} from "@/helpers";
import {
  FORM_APPLICATION_EXPORT_COLUMNS,
  getFormApplicationExportColumnValue,
} from "../../_constants/forms-export.const";
import type {
  FormApplication,
  FormApplicationExportColumnKey,
  FormApplicationPeriod,
  FormApplicationStatus,
  FormApplicationTypeFilter,
} from "../../_types/formsApplications.types";

type FormExportModalProps = {
  isOpen: boolean;
  type: FormApplicationTypeFilter;
  period: FormApplicationPeriod;
  status: FormApplicationStatus;
  search: string;
  onClose: () => void;
};

export const FormExportModal = ({
  isOpen,
  type,
  period,
  status,
  search,
  onClose,
}: FormExportModalProps) => {
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [selectedExportColumns, setSelectedExportColumns] = useState<
    Array<FormApplicationExportColumnKey>
  >(
    () =>
      FORM_APPLICATION_EXPORT_COLUMNS.filter(
        (column) => column.defaultSelected,
      ).map((column) => column.key) as Array<FormApplicationExportColumnKey>,
  );

  const exportOptions = useMemo(
    () =>
      FORM_APPLICATION_EXPORT_COLUMNS.map((column) => ({
        key: column.key,
        label: column.label,
        description: column.description,
      })),
    [],
  );

  const handleClose = () => {
    if (isExporting) return;
    setExportError(null);
    onClose();
  };

  const handleToggleExportColumn = (key: string, checked: boolean) => {
    setExportError(null);
    setSelectedExportColumns((currentColumns) => {
      const typedKey = key as FormApplicationExportColumnKey;

      if (checked) {
        return currentColumns.includes(typedKey)
          ? currentColumns
          : [...currentColumns, typedKey];
      }

      return currentColumns.filter((columnKey) => columnKey !== typedKey);
    });
  };

  const handleSelectAllExportColumns = () => {
    setExportError(null);
    setSelectedExportColumns(
      FORM_APPLICATION_EXPORT_COLUMNS.map((column) => column.key),
    );
  };

  const handleClearExportColumns = () => {
    setExportError(null);
    setSelectedExportColumns([]);
  };

  const handleExportForms = async () => {
    if (!selectedExportColumns.length) {
      setExportError("Select at least one field to export.");
      return;
    }

    try {
      setIsExporting(true);
      setExportError(null);

      const response = await axiosInstance.get<
        ApiResponse<Array<FormApplication>>
      >("/v2/inbox/forms/applications/export", {
        params: {
          type,
          period,
          status,
          ...(search.trim().length > 0 ? { q: search.trim() } : {}),
        },
      });

      const columns = FORM_APPLICATION_EXPORT_COLUMNS.filter((column) =>
        selectedExportColumns.includes(column.key),
      );
      const csvContent = createCsvContent(
        columns.map((column) => column.label),
        (response.data.data ?? []).map((application) =>
          columns.map((column) =>
            getFormApplicationExportColumnValue(application, column),
          ),
        ),
      );

      downloadCsvFile(
        `forms-${new Date().toISOString().slice(0, 10)}.csv`,
        csvContent,
      );

      onClose();
    } catch (error) {
      setExportError(
        handleAxiosError(error)?.message ??
          "Failed to export form applications. Please try again.",
      );
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <ExportModal
      isOpen={isOpen}
      title="Export forms"
      description="Choose which form fields should be included in the CSV export."
      options={exportOptions}
      selectedKeys={selectedExportColumns}
      isExporting={isExporting}
      errorMessage={exportError}
      onClose={handleClose}
      onToggleOption={handleToggleExportColumn}
      onSelectAll={handleSelectAllExportColumns}
      onClearAll={handleClearExportColumns}
      onExport={handleExportForms}
    />
  );
};
